import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { AlertTriangle, AlertCircle, Info, ExternalLink, X } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SystemAlertsBanner() {
  const { user, sessionToken } = useAuthStore();
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Only fetch for admins
    if (!user?.is_admin) {
      setLoading(false);
      return;
    }

    const fetchSystemStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/system-status`, {
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setAlerts(data.alerts || []);
        }
      } catch (error) {
        console.error('Failed to fetch system status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSystemStatus();
    
    // Check every hour
    const interval = setInterval(fetchSystemStatus, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, sessionToken]);

  // Don't show for non-admins or if loading
  if (!user?.is_admin || loading) {
    return null;
  }

  // Filter out dismissed alerts
  const visibleAlerts = alerts.filter(alert => 
    !dismissed.includes(`${alert.category}-${alert.type}`)
  );

  if (visibleAlerts.length === 0) {
    return null;
  }

  const getAlertStyles = (type) => {
    switch (type) {
      case 'error':
        return 'bg-brick-soft text-brick';
      case 'warning':
        return 'bg-ochre-soft text-ochre';
      case 'info':
      default:
        return 'bg-mist text-foreground';
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'error':
        return <AlertCircle className="w-[18px] h-[18px] text-brick shrink-0" />;
      case 'warning':
        return <AlertTriangle className="w-[18px] h-[18px] text-ochre shrink-0" />;
      case 'info':
      default:
        return <Info className="w-[18px] h-[18px] text-sky shrink-0" />;
    }
  };

  const handleDismiss = (alert) => {
    setDismissed(prev => [...prev, `${alert.category}-${alert.type}`]);
  };

  return (
    <div className="space-y-3 mb-6">
      {visibleAlerts.map((alert, index) => (
        <div
          key={`${alert.category}-${alert.type}-${index}`}
          className={cn(
            'relative flex items-start gap-3 px-4 py-3 rounded-cards',
            getAlertStyles(alert.type)
          )}
        >
          {getIcon(alert.type)}
          
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">{alert.title}</p>
            <p className="text-sm opacity-80 mt-0.5">{alert.message}</p>
            
            {alert.action_url && (
              <a
                href={alert.action_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium mt-2 hover:underline"
              >
                {alert.action_label || 'Open dashboard'}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          
          <button
            onClick={() => handleDismiss(alert)}
            className="p-1 rounded-pill hover:bg-ink/5 transition-colors shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 opacity-60" />
          </button>
        </div>
      ))}
    </div>
  );
}
